// Direct browser client for the OpZero platform MCP endpoint (website
// app/api/mcp/route.ts — a Streamable HTTP MCP route). Same convention as
// HUB_BASE_URL in hubApi.ts: the URL is hardcoded because the SPA may be served
// from anywhere (local bun server or the hub Worker) while the platform lives at
// opzero.sh. Auth is the same MCPAuthKit mat_ token used for the hub.

import type {
  PlatformAssistant,
  PlatformDeployment,
  PlatformProject,
  PlatformProjectDetail,
  PlatformServerLogs,
} from "./platformTypes";

const PLATFORM_MCP_URL = "https://opzero.sh/api/mcp";

export class PlatformApiError extends Error {
  insufficientScope: boolean;
  unauthorized: boolean;

  constructor(message: string, opts?: { insufficientScope?: boolean; unauthorized?: boolean }) {
    super(message);
    this.name = "PlatformApiError";
    this.insufficientScope = opts?.insufficientScope ?? false;
    this.unauthorized = opts?.unauthorized ?? false;
  }
}

interface JsonRpcResponse {
  jsonrpc?: string;
  id?: string | number | null;
  result?: {
    content?: Array<{ type: string; text: string }>;
    isError?: boolean;
  };
  error?: { code?: number; message?: string };
}

let rpcId = 0;

// The route answers POST tools/call with a plain JSON body today; the SSE
// branch handles a future switch to Streamable HTTP single-response framing
// (`data: {...}` lines) without breaking callers.
function parseRpcBody(raw: string, contentType: string): JsonRpcResponse {
  if (contentType.includes("text/event-stream")) {
    for (const line of raw.split("\n")) {
      if (line.startsWith("data:")) {
        return JSON.parse(line.slice(5).trim()) as JsonRpcResponse;
      }
    }
    throw new PlatformApiError("Platform MCP returned an empty event stream");
  }
  return JSON.parse(raw) as JsonRpcResponse;
}

function toPlatformError(message: string): PlatformApiError {
  return new PlatformApiError(message, {
    insufficientScope: message.includes("Insufficient scope"),
  });
}

export async function callTool<T>(
  token: string,
  name: string,
  args: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(PLATFORM_MCP_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: ++rpcId,
      method: "tools/call",
      params: { name, arguments: args },
    }),
  });

  if (res.status === 401) {
    // Surface a status so withFreshHubToken-style 401-retry wrappers work.
    const err = new PlatformApiError("Platform authorization failed", { unauthorized: true });
    (err as PlatformApiError & { status?: number }).status = 401;
    throw err;
  }

  const raw = await res.text();
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(raw) as JsonRpcResponse;
      if (parsed.error?.message) message = parsed.error.message;
    } catch {
      if (raw) message = raw;
    }
    const err = toPlatformError(message);
    (err as PlatformApiError & { status?: number }).status = res.status;
    throw err;
  }

  const body = parseRpcBody(raw, res.headers.get("content-type") ?? "");
  if (body.error) {
    throw toPlatformError(body.error.message ?? "Platform MCP error");
  }

  const text = body.result?.content?.[0]?.text;
  if (typeof text !== "string") {
    throw new PlatformApiError("Platform MCP returned no tool content");
  }

  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new PlatformApiError(`Platform MCP returned non-JSON content: ${text.slice(0, 200)}`);
  }

  const failed =
    body.result?.isError === true ||
    (typeof payload === "object" && payload !== null &&
      (payload as { success?: boolean }).success === false);
  if (failed) {
    const message =
      (typeof payload === "object" && payload !== null &&
        typeof (payload as { error?: unknown }).error === "string"
        ? (payload as { error: string }).error
        : null) ?? "Platform tool call failed";
    throw toPlatformError(message);
  }

  return payload as T;
}

// ---- Typed wrappers. Arg names mirror mcps/deploy/src/tools/schemas.ts exactly
// (mostly snake_case; list_deployments is the camelCase outlier).

export interface ListProjectsResult {
  success: true;
  count: number;
  projects: PlatformProject[];
}

export interface ProjectDetailResult {
  success: true;
  project: PlatformProjectDetail;
}

export interface ListDeploymentsResult {
  success: true;
  count: number;
  deployments: PlatformDeployment[];
}

export interface RedeployResult {
  success: true;
  url: string;
  projectId: string;
  deploymentId: string;
  message: string;
}

export interface RollbackResult {
  success: true;
  url: string;
  projectId: string;
  projectName: string;
  deploymentId: string;
  rolledBackFrom: string;
  message: string;
}

export interface ListAssistantsResult {
  success: true;
  count: number;
  assistants: PlatformAssistant[];
}

export interface AssistantResult {
  success: true;
  assistant: PlatformAssistant;
  message?: string;
}

export interface RotateTokenResult {
  success: true;
  projectId: string;
  endpointUrl: string | null;
  accessToken: string;
  accessTokenNote: string;
  message: string;
}

export interface AssistantPatch {
  name?: string;
  model?: string;
  system_prompt?: string;
  mcp_servers?: Array<{ url: string; name: string; headers?: Record<string, string> }>;
  max_tokens?: number;
  max_steps?: number;
  rate_limit_per_minute?: number;
}

export const platformApi = {
  listProjects: (token: string, status: "active" | "archived" | "deleted" | "all" = "all") =>
    callTool<ListProjectsResult>(token, "list_projects", { status, limit: 100 }),

  getProjectDetails: (token: string, projectId: string) =>
    callTool<ProjectDetailResult>(token, "get_project_details", { project_id: projectId }),

  listDeployments: (token: string, projectId: string, limit = 20) =>
    callTool<ListDeploymentsResult>(token, "list_deployments", { projectId, limit }),

  redeploy: (token: string, projectId: string) =>
    callTool<RedeployResult>(token, "redeploy", { project_id: projectId }),

  rollbackDeployment: (token: string, deploymentId: string) =>
    callTool<RollbackResult>(token, "rollback_deployment", { deployment_id: deploymentId }),

  deleteDeploymentRecord: (token: string, deploymentId: string) =>
    callTool<{ success: true; deletedDeploymentId: string; message: string }>(
      token, "delete_deployment_record", { deployment_id: deploymentId },
    ),

  projectDelete: (token: string, projectId: string) =>
    callTool<{ success: true; deleted: Array<{ id: string; name: string; url: string | null }>; count: number; message: string }>(
      token, "project_delete", { project_id: projectId },
    ),

  projectArchive: (token: string, projectId: string, action: "archive" | "unarchive" = "archive") =>
    callTool<{ success: true; affected: Array<{ id: string; name: string; previousStatus: string | null; newStatus: string }>; count: number; message: string }>(
      token, "project_archive", { project_id: projectId, action },
    ),

  projectCleanup: (token: string) =>
    callTool<{ success: true; recommendations: string[] } & Record<string, unknown>>(
      token, "project_cleanup", {},
    ),

  setCustomDomain: (token: string, projectId: string, domain: string) =>
    callTool<{ success: true; projectId: string; customDomain: string | null } & Record<string, unknown>>(
      token, "set_custom_domain", { project_id: projectId, domain },
    ),

  listAssistants: (token: string, status: "active" | "archived" | "all" = "all") =>
    callTool<ListAssistantsResult>(token, "list_assistants", { status, limit: 100 }),

  getAssistant: (token: string, assistantId: string) =>
    callTool<AssistantResult>(token, "get_assistant", { assistant_id: assistantId }),

  updateAssistant: (token: string, assistantId: string, patch: AssistantPatch) =>
    callTool<AssistantResult>(token, "update_assistant", { assistant_id: assistantId, ...patch }),

  deleteAssistant: (token: string, assistantId: string) =>
    callTool<{ success: true; deletedId: string; deletedSlug: string; message: string }>(
      token, "delete_assistant", { assistant_id: assistantId },
    ),

  listMcpServerSecrets: (token: string, projectId: string) =>
    callTool<{ success: true; keys: string[] }>(
      token, "list_mcp_server_secrets", { project_id: projectId },
    ),

  setMcpServerSecret: (token: string, projectId: string, key: string, value: string) =>
    callTool<{ success: true; hotApplied: boolean; message: string }>(
      token, "set_mcp_server_secret", { project_id: projectId, key, value },
    ),

  deleteMcpServerSecret: (token: string, projectId: string, key: string) =>
    callTool<{ success: true; hotApplied: boolean; message: string }>(
      token, "delete_mcp_server_secret", { project_id: projectId, key },
    ),

  rotateMcpServerToken: (token: string, projectId: string) =>
    callTool<RotateTokenResult>(token, "rotate_mcp_server_token", { project_id: projectId }),

  getMcpServerLogs: (token: string, projectId: string, limit = 10) =>
    callTool<{ success: true } & PlatformServerLogs>(
      token, "get_mcp_server_logs", { project_id: projectId, limit },
    ),
};
